import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { DataCard, Loader, SdiShipMoving, styles } from '@sdir/sds';
import { useNumberService } from '@sdir/utilities/lib/hooks';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import styled from 'styled-components';
import moment from 'moment';
import { SeagoingVesselPreviewLink } from '@sdir/blueprint';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { InfoPageLayout } from '../../Organisms';
import {
  SeagoingRow,
  SeagoingTableHeader,
  SeagoingChildTableHeader,
  SeagoingChildTableRow,
  TableDefinition,
  SeagoingChildTableHeaderMobile,
} from '../../../helpers/TableHeaders';
import { seagoingTimeCalculatorApi } from '../../../httpclient';
import { DisplayDateFormat } from '../../../helpers';
import { SortableDataTable } from '../../Molecules';
import { SeagoingLogInfo } from './SeagoingLogInfo';

const getOwnSeagoingTimeApiCall =
  seagoingTimeCalculatorApi?.v1SeagoingtimecalculatorGetownseagoingtimeQueryPost.bind(
    seagoingTimeCalculatorApi
  );

export const SeagoingTimePage = () => {
  const fm = useIntl().formatMessage;
  const numberService = useNumberService();
  const [includeExpired, setIncludeExpired] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { result, loading, errorObject, callApi } = useApi({
    apiFunction: getOwnSeagoingTimeApiCall,
  });

  const seagoingTable = useMemo<TableDefinition<SeagoingRow> | null>(() => {
    if (!result) return null;

    return {
      columns: SeagoingTableHeader(fm),
      data: result?.map<SeagoingRow>((x) => {
        const childTable: TableDefinition<SeagoingChildTableRow> = {
          columns: isMobile ? SeagoingChildTableHeaderMobile(fm) : SeagoingChildTableHeader(fm),
          data:
            x.details?.map((d) => {
              return {
                employer: d.organizationName || '',
                vessel: d.vessel ? (
                  <SeagoingVesselPreviewLink
                    vessel={d.vessel}
                    accumulatedData={d.accumulatedData || null}
                  />
                ) : null,
                vesselValue: d.vessel?.name || '',
                period: `${d.signOnDate ? DisplayDateFormat(d.signOnDate) : ''} - ${
                  d.signOffDate ? DisplayDateFormat(d.signOffDate) : ''
                }`,
                periodStart: moment(d.signOnDate),
                periodEnd: moment(d.signOffDate),
                days: numberService.formatDecimal(d.seagoingDays || 0),
                daysValue: d.seagoingDays || 0,
                details: <SeagoingLogInfo report={x} detail={d} />,
              };
            }) || [],
          sortableKeys: SeagoingChildTableHeader(fm)
            .map((c) => c.key)
            .filter((c) => c !== 'details'),
        };
        return {
          occupation: x.serviceDescription || '',
          days: numberService.formatDecimal(x.seagoingDays || 0),
          daysValue: x.seagoingDays || 0,
          childContent: <SortableDataTable className="child-table" table={childTable} />,
        };
      }),
      sortableKeys: '*',
      collapsable: true,
      collapsableText: [
        fm({ id: 'general.table.viewdetails' }),
        fm({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [fm, numberService, result, isMobile]);

  const toggleExpired = useCallback(() => setIncludeExpired(!includeExpired), [includeExpired]);

  useEffect(() => {
    callApi({
      take: 500,
      includeOutdatedReports: includeExpired,
    });
  }, [callApi, includeExpired]);

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <InfoPageLayout
      title={fm({ id: 'seagoing.title' })}
      description={fm({ id: 'seagoing.description' }, { br: () => <br /> })}
      icon={<SdiShipMoving color={styles.colors.secondary.seaBlue4} size="l" />}
      extraInfoTitle={fm({ id: 'seagoing.missingseagoingtime' })}
      extraInfoDescription={fm(
        { id: 'seagoing.missingseagoingtimedescription' },
        { br: () => <br /> }
      )}
    >
      <DataCard
        title={fm({ id: 'seagoing.title' })}
        expandable
        onExpandClicked={toggleExpired}
        linkText={fm({
          id: includeExpired
            ? 'seagoing.toggle.hide.five.year.old'
            : 'seagoing.toggle.show.five.year.old',
        })}
      >
        {loading && <Loader />}
        {errorObject && <ErrorMessage error={errorObject} />}
        {seagoingTable && (
          <TableWrapper data-testid="seagoing-table">
            <SeagoingTable table={seagoingTable} />
          </TableWrapper>
        )}
      </DataCard>
    </InfoPageLayout>
  );
};

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const SeagoingTable = styled(SortableDataTable)`
  &&& {
    .open-row-content {
      padding: 0 2rem 0 0;
    }

    table > tbody > tr > td:last-child {
      text-align: right;
      > * {
        text-align: left;
      }

      .child-table {
        background: none;
        padding: 0 0 3rem 0;
      }
    }
  }
`;
